import api from './client';

export interface AnalyticsSummary {
  totalPosts: number;
  publishedPosts: number;
  successRate: number;
  failedJobs: number;
  scheduledPosts: number;
  pendingReviews: number;
  totalAccounts: number;
  healthyAccounts: number;
  activeCampaigns: number;
}

export interface AccountGrowthReport {
  days: number;
  generatedAt: string;
  recommendationSource?: 'gemini' | 'rules';
  totals: {
    accounts: number;
    accountsWithData: number;
    followerGrowth: number;
    impressionGrowth: number;
    reachGrowth: number;
    engagementGrowth: number;
    clickGrowth: number;
    conversionGrowth: number;
    posts: number;
    failedJobs: number;
  };
  accounts: Array<{
    accountId: string;
    platform: string;
    name: string;
    handle: string;
    status: string;
    tokenHealth: string;
    days: number;
    dataAvailable: boolean;
    startFollowers: number;
    endFollowers: number;
    followerGrowth: number;
    followerGrowthPct: number;
    impressionGrowth: number;
    reachGrowth: number;
    engagementGrowth: number;
    clickGrowth: number;
    conversionGrowth: number;
    postCount: number;
    publishedPosts: number;
    scheduledPosts: number;
    failedJobs: number;
    snapshots: Array<{ date: string; followers: number; impressions: number; reach: number; engagement: number; clicks: number; conversions: number; source: string }>;
    recommendations: string[];
  }>;
  recommendations: string[];
}

export async function getAnalyticsSummary(workspaceId?: string): Promise<AnalyticsSummary> {
  const params = workspaceId ? `?workspaceId=${workspaceId}` : '';
  return api.get(`/analytics/summary${params}`);
}

export async function getCampaignAnalytics(workspaceId?: string): Promise<any[]> {
  const params = workspaceId ? `?workspaceId=${workspaceId}` : '';
  return api.get(`/analytics/campaigns${params}`);
}

export async function getPlatformAnalytics(workspaceId?: string): Promise<Record<string, number>> {
  const params = workspaceId ? `?workspaceId=${workspaceId}` : '';
  return api.get(`/analytics/platforms${params}`);
}

export async function getDashboardSummary(workspaceId?: string): Promise<{
  accountCount: number;
  activeCampaigns: number;
  pendingReviews: number;
  queuedPublishJobs: number;
  failedPublishJobs: number;
  recentAuditLogs: any[];
  upcomingScheduledPosts: any[];
}> {
  const params = workspaceId ? `?workspaceId=${workspaceId}` : '';
  return api.get(`/dashboard/summary${params}`);
}

export async function getAccountGrowthReport(workspaceId: string, days = 30): Promise<AccountGrowthReport> {
  return api.get(`/reports/account-growth?workspaceId=${workspaceId}&days=${days}`);
}

export function getAccountGrowthExportUrl(workspaceId: string, days = 30, format: 'csv' | 'html' = 'csv') {
  return `/api/reports/account-growth/export?workspaceId=${encodeURIComponent(workspaceId)}&days=${days}&format=${format}`;
}

export async function syncAccountInsights(workspaceId: string, accountId?: string): Promise<any> {
  return api.post('/reports/sync-insights', { workspaceId, accountId });
}

export async function saveAccountMetricSnapshot(data: {
  workspaceId: string;
  socialAccountId: string;
  snapshotDate: string;
  followers?: number;
  following?: number;
  posts?: number;
  impressions?: number;
  reach?: number;
  engagement?: number;
  clicks?: number;
  conversions?: number;
  source?: string;
  notes?: string;
}): Promise<any> {
  return api.post('/reports/account-metrics', data);
}
